import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users, ClipboardList, Loader2, Eye, AlertTriangle, Upload } from 'lucide-react';
import BulkClientImportDialog from '../../components/admin/BulkClientImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [desyncIds, setDesyncIds] = useState(new Set());
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '', notes: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchPlans();
  }, []);

  const fetchClients = async () => {
    try {
      const [resClients, resDesync] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/trainer/clients/desync-check`).catch(() => ({ data: { desync_client_ids: [] } })),
      ]);
      setClients(resClients.data);
      setDesyncIds(new Set(resDesync.data?.desync_client_ids || []));
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (selectedClient) {
        await axios.put(`${API}/clients/${selectedClient.id}`, {
          name: formData.name,
          phone: formData.phone,
          notes: formData.notes
        });
        toast.success(t('clientUpdated'));
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success(t('clientCreated'));
      }
      setDialogOpen(false);
      fetchClients();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clients/${selectedClient.id}`);
      toast.success(t('clientDeleted'));
      setDeleteDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedPlan) return;
    try {
      await axios.post(`${API}/plans/assign`, {
        client_id: selectedClient.id,
        plan_id: selectedPlan
      });
      toast.success(t('planAssigned'));
      setAssignDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUnassignPlan = async (clientId, planId) => {
    try {
      await axios.post(`${API}/plans/unassign`, {
        client_id: clientId,
        plan_id: planId
      });
      toast.success(t('success'));
      fetchClients();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      password: '',
      phone: client.phone || '',
      notes: client.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedClient(null);
    setFormData({ name: '', email: '', password: '', phone: '', notes: '' });
    setAcceptTerms(false);
    setAcceptResponsibility(false);
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.email.toLowerCase().includes(search.toLowerCase())
  );

  const getPlanName = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.name || planId;
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('clients')}</h1>
            <p className="text-muted-foreground">{filteredClients.length} {t('clients').toLowerCase()}</p>
          </div>
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="new-client-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('newClient')}
          </Button>
        </div>

        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-import-clients-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Carga masiva (CSV)
          </Button>
        </div>

        <BulkClientImportDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onCreated={fetchClients}
          role="admin"
        />

        {/* Banner desincronizados */}
        {desyncIds.size > 0 && (
          <div
            className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-md p-3 text-sm"
            data-testid="desync-banner"
          >
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <strong className="text-red-300">{desyncIds.size} cliente{desyncIds.size !== 1 ? 's' : ''} desincronizado{desyncIds.size !== 1 ? 's' : ''}</strong>
              <p className="text-xs text-muted-foreground mt-0.5">
                El plan del macro activo no coincide con el plan asignado. Abre cada uno y usa <strong>"Posición en el Macro"</strong> para reposicionarlo.
              </p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
            data-testid="client-search-input"
          />
        </div>

        {/* Clients Table */}
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t('noClients')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">{t('clientName')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('email')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('phone')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('assignedPlans')}</TableHead>
                    <TableHead className="text-muted-foreground text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow 
                      key={client.id} 
                      className="border-border table-row-hover"
                      data-testid={`client-row-${client.id}`}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          {client.name}
                          {!client.onboarding_completed && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-medium"
                              title="El cliente aún no ha completado el cuestionario de onboarding"
                              data-testid={`onboarding-pending-${client.id}`}
                            >
                              ⚠ Onboarding
                            </span>
                          )}
                          {desyncIds.has(client.id) && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-medium"
                              title="Posible desincronización del macro. Abre el cliente y usa 'Posición en el Macro'."
                              data-testid={`desync-badge-${client.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" /> Desincronizado
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{client.email}</TableCell>
                      <TableCell className="text-muted-foreground">{client.phone || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {client.assigned_plans?.length > 0 ? (
                            client.assigned_plans.map(planId => (
                              <span 
                                key={planId}
                                className="inline-flex items-center px-2 py-1 text-xs bg-primary/10 text-primary rounded-sm cursor-pointer hover:bg-primary/20"
                                onClick={() => handleUnassignPlan(client.id, planId)}
                                title={t('unassignPlan')}
                              >
                                {getPlanName(planId)}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/admin/clients/${client.id}`)}
                            className="text-primary"
                            data-testid={`view-client-${client.id}`}
                            title="Ver registros"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedClient(client); setAssignDialogOpen(true); }}
                            data-testid={`assign-plan-${client.id}`}
                          >
                            <ClipboardList className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedClient(client); setDeleteDialogOpen(true); }}
                            className="hover:text-destructive"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{selectedClient ? t('editClient') : t('newClient')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('clientName')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="client-name-input"
                />
              </div>
              {!selectedClient && (
                <>
                  <div className="space-y-2">
                    <Label>{t('email')}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-zinc-900/50 border-zinc-800"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('password')}</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="bg-zinc-900/50 border-zinc-800"
                      data-testid="client-password-input"
                    />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>{t('phone')}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="client-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="client-notes-input"
                />
              </div>
              
              {/* Legal Checkboxes - Only for new clients */}
              {!selectedClient && (
                <div className="space-y-4 pt-2 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500">El cliente declara que:</p>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="accept-terms"
                      checked={acceptTerms}
                      onCheckedChange={setAcceptTerms}
                      className="mt-0.5"
                      data-testid="accept-terms-checkbox"
                    />
                    <label 
                      htmlFor="accept-terms" 
                      className="text-sm text-zinc-300 leading-tight cursor-pointer"
                    >
                      He leído y acepto los{' '}
                      <Link 
                        to="/legal/terminos-uso" 
                        target="_blank" 
                        className="text-primary hover:underline"
                      >
                        Términos de Uso
                      </Link>{' '}
                      y la{' '}
                      <Link 
                        to="/legal/privacidad" 
                        target="_blank" 
                        className="text-primary hover:underline"
                      >
                        Política de Privacidad
                      </Link>
                    </label>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="accept-responsibility"
                      checked={acceptResponsibility}
                      onCheckedChange={setAcceptResponsibility}
                      className="mt-0.5"
                      data-testid="accept-responsibility-checkbox"
                    />
                    <label 
                      htmlFor="accept-responsibility" 
                      className="text-sm text-zinc-300 leading-tight cursor-pointer"
                    >
                      Declaro conocer y aceptar las cláusulas de{' '}
                      <Link 
                        to="/legal/bloqueo-suspension" 
                        target="_blank" 
                        className="text-primary hover:underline"
                      >
                        bloqueo de cuenta
                      </Link>{' '}
                      y la{' '}
                      <Link 
                        to="/legal/exencion-responsabilidad" 
                        target="_blank" 
                        className="text-primary hover:underline"
                      >
                        exención de responsabilidad deportiva
                      </Link>
                    </label>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="bg-primary text-primary-foreground"
                  disabled={submitting || (!selectedClient && (!acceptTerms || !acceptResponsibility))}
                  data-testid="save-client-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation - Requires typing ELIMINAR */}
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          title="Eliminar cliente"
          description="Esta acción eliminará permanentemente al cliente y todos sus datos asociados."
          itemName={selectedClient?.name}
        />

        {/* Assign Plan Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{t('assignPlan')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-muted-foreground">{selectedClient?.name}</p>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-plan-trigger">
                  <SelectValue placeholder={t('selectPlan')} />
                </SelectTrigger>
                <SelectContent>
                  {plans.filter(p => !selectedClient?.assigned_plans?.includes(p.id)).map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleAssignPlan}
                className="bg-primary text-primary-foreground"
                disabled={!selectedPlan}
                data-testid="confirm-assign-button"
              >
                {t('assignPlan')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ClientsPage;
